import { apiRequest, authStorage } from './api';
import { toPortableUploadedAssetPath } from '../utils/brandingUrl';

const USER_KEY = 'onego_user';
const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3000/api').replace(/\/$/, '');

function persistAuth(data) {
  const tokens = data?.tokens || null;
  const user = data?.user || null;

  if (tokens) {
    authStorage.setTokens(tokens);
  }

  if (user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  return { user, tokens };
}

function sanitizeRegisterPayload(formData) {
  const trimmedCommune = (formData.commune || '').trim();
  const payload = {
    email: (formData.email || '').trim().toLowerCase(),
    motdepasse: formData.motdepasse,
    confirmMotdepasse: formData.confirmMotdepasse,
    role: formData.role,
    prenom: (formData.prenom || '').trim(),
    nom: (formData.nom || '').trim(),
    postnom: (formData.postnom || '').trim() || undefined,
    sexe: formData.sexe,
    dateNaissance: formData.dateNaissance,
    ville: (formData.ville || '').trim(),
    commune: trimmedCommune,
    quartier: (formData.quartier || trimmedCommune || '').trim(),
    detailsRue: (formData.detailsRue || '').trim(),
    typeCompte: formData.typeCompte || formData.typeClient || 'STANDARD',
    typeClient: formData.typeClient,
    acceptationConditions: formData.acceptationConditions,
    metadata: formData.metadata || undefined,
    livreurZoneIds: formData.livreurZoneIds || undefined,
  };
  if (formData.telephone?.trim()) {
    payload.telephone = formData.telephone.trim();
  }
  const codeParrain = (formData.codeParrain || '').trim().toUpperCase();
  if (codeParrain) {
    payload.codeParrain = codeParrain;
  }
  const categorieManuelle = (formData.categorieManuelle || '').trim();
  if (categorieManuelle) {
    payload.categorieManuelle = categorieManuelle;
  }
  const profession = (formData.profession || '').trim();
  if (profession) {
    payload.profession = profession;
  }
  return payload;
}

export const authService = {
  async loginWithGoogle(idToken) {
    const response = await apiRequest('/auth/google', {
      method: 'POST',
      body: JSON.stringify({
        idToken,
        role: 'APPRENANT',
      }),
    });

    const authState = persistAuth(response.data);
    const profile = await this.getCurrentUser();

    return {
      ...authState,
      user: profile,
      isNewUser: Boolean(response.data?.isNewUser),
    };
  },

  async login({ email, motdepasse }) {
    const response = await apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({
        email: (email || '').trim().toLowerCase(),
        motdepasse,
      }),
    });

    const authState = persistAuth(response.data);
    const profile = await this.getCurrentUser();

    return {
      ...authState,
      user: profile,
    };
  },

  async register(formData) {
    const payload = sanitizeRegisterPayload(formData);
    const response = await apiRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    const registerData = response.data;
    if (registerData?.otpRequired) {
      return {
        otpRequired: true,
        email: registerData.email || '',
        message: registerData.message || 'Code OTP envoyé par email.',
      };
    }

    const authState = persistAuth(registerData);
    return { ...authState, otpRequired: false };
  },

  async verifyOtp({ email, otp }) {
    const response = await apiRequest('/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ email, otp }),
    });
    const authState = persistAuth(response.data);
    const profile = await this.getCurrentUser();
    return {
      ...authState,
      user: profile,
    };
  },

  async requestOtp({ email }) {
    await apiRequest('/auth/request-otp', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  async getZones() {
    const response = await apiRequest('/zones', {
      method: 'GET',
    });
    return Array.isArray(response.data) ? response.data : [];
  },

  async getProductCategories() {
    const response = await apiRequest('/products/categories', {
      method: 'GET',
    });
    return Array.isArray(response.data) ? response.data : [];
  },

  async uploadRegistrationDocument(file) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiRequest('/auth/upload-registration-document', {
      method: 'POST',
      body: formData,
      withAuth: false,
    });

    const path = response?.data?.path;
    const uploadToken = response?.data?.uploadToken;
    if (typeof path === 'string' && path.startsWith('/uploads/') && typeof uploadToken === 'string' && uploadToken) {
      return { path, uploadToken };
    }
    throw new Error('Réponse upload invalide');
  },

  async uploadSellerDocument(file) {
    const formData = new FormData();
    formData.append('file', file);

    const accessToken = authStorage.getAccessToken();
    const headers = {};
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    const response = await fetch(`${API_BASE_URL}/seller/upload-image`, {
      method: 'POST',
      headers,
      body: formData,
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.message || "Echec de l'upload du document.");
    }

    const url = payload?.data?.url;
    if (!url) {
      throw new Error("Aucune URL n'a été retournée après upload.");
    }
    return url;
  },

  async getCurrentUser() {
    const response = await apiRequest('/auth/me', {
      method: 'GET',
      withAuth: true,
    });

    const user = response.data?.user || null;

    if (user) {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    }

    return user;
  },

  async updateProfile(profilePatch) {
    const response = await apiRequest('/auth/profile', {
      method: 'PATCH',
      withAuth: true,
      body: JSON.stringify(profilePatch || {}),
    });
    const user = response?.data?.user || null;
    if (user) {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    }
    return user;
  },

  async changePassword({ ancienMotdepasse, nouveauMotdepasse, confirmMotdepasse }) {
    const response = await apiRequest('/auth/change-password', {
      method: 'POST',
      withAuth: true,
      body: JSON.stringify({
        ancienMotdepasse,
        nouveauMotdepasse,
        confirmMotdepasse,
      }),
    });
    const profile = await this.getCurrentUser();
    return { ...response, user: profile };
  },

  async uploadProfileImage(file) {
    const formData = new FormData();
    formData.append('file', file);

    const accessToken = authStorage.getAccessToken();
    const headers = {};
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    const response = await fetch(`${API_BASE_URL}/profile/upload-image`, {
      method: 'POST',
      headers,
      body: formData,
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.message || "Echec de l'upload de la photo de profil.");
    }

    const raw = payload?.data?.path || payload?.data?.url;
    if (!raw) {
      throw new Error("Aucune URL n'a été retournée après upload.");
    }
    return toPortableUploadedAssetPath(String(raw)) || String(raw);
  },

  async logout() {
    try {
      await apiRequest('/auth/logout', {
        method: 'POST',
        withAuth: true,
      });
    } finally {
      this.clearAuth();
    }
  },

  getStoredAuth() {
    const serializedUser = localStorage.getItem(USER_KEY);

    return {
      user: serializedUser ? JSON.parse(serializedUser) : null,
      tokens: {
        accessToken: authStorage.getAccessToken(),
        refreshToken: authStorage.getRefreshToken(),
      },
    };
  },

  clearAuth() {
    authStorage.clearTokens();
    localStorage.removeItem(USER_KEY);
  },
};
